/**
 * @file payments-to-vendors.tsx
 * @deprecated Copied to app/modules/procurement/pages/vendors_management/payments/payments-to-vendors.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/vendors_management/payments/payments-to-vendors
 * @description Page component for managing payments to vendors in the vendors management section.
 *   Displays a list of payments to vendors with pagination, search functionality, and CRUD operations.
 *   Allows users to view the details of payments to vendors with warehouse context integration.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo Permission constant: uses PaymentToVendors module (correct for uTrade); payment-completed.tsx
 *   still references PaymentToSellers — align across payments/* siblings.
 */

import { Button, Card, Col, Row } from "react-bootstrap";
import { KTSVG } from "../../../../../_admin/helpers";
// import { BrandAndTotalSales } from "../../../../utils/assetsAndPermissions";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  PAGE_LIMIT,
  PaymentToSellers,
  PermissionModule,
  Permissions,
  View,
} from "../../../../../utils/constants";
import { seller } from "../../../../../api/apiEndPoints";
import { sellerJSON } from "../../../../../api/apiJSON/seller";
import APICallService from "../../../../../api/apiCallService";
import Pagination from "../../../../../Global/pagination";
import Method from "../../../../../utils/methods";
import { getKey, removeKey, setKey } from "../../../../../Global/history";
import {
  listPaymentToSeller,
  listSellerFinanceDetails,
} from "../../../../../utils/storeString";
import secureLocalStorage from "react-secure-storage";
import SellerPayoutReport from "../../../../modals/report/seller-payout";
import { ElementAnimateUtil } from "../../../../../_admin/assets/ts/_utils";
import { useAuth } from "../../../auth";
import { useWarehouse } from "../../../../context/WarehouseContext";
import { useDebounce } from "../../../../../utils/useDebounce";
import { ColumnDef } from "@tanstack/react-table";
import { CustomTable } from "../../../../custom/data-table";
import { useNavigationHistory } from "../../../../../utils/useNavigationHistory";
const PaymentsToVendors = () => {
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const { navigateTo } = useNavigationHistory();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [paymentData, setPaymentData] = useState<any>([]);
  const [search, setSearch] = useState<string>(
    getKey(listPaymentToSeller.search)?.toString() || ""
  );
  const [showDownload, setShowDownload] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState<any>(getKey(listPaymentToSeller.page) || 1);
  const [pageLimit, setPageLimit] = useState<any>(
    getKey(listPaymentToSeller.limit) || PAGE_LIMIT
  );
  const canViewDetails = Method.hasPermission(
    PermissionModule.PaymentToVendors,
    View,
    currentUser
  );
  useEffect(() => {
    (async () => {
      setFetchLoading(true);
      if (!Method.hasModulePermission(PermissionModule.PaymentToVendors,currentUser)) {
        return window.history.back();
      }
      secureLocalStorage.removeItem(listSellerFinanceDetails.page);
      localStorage.removeItem("businessId");
      localStorage.removeItem("details");
      await fetchPayment(page, pageLimit, search);
      setDataFetched(true);
      setFetchLoading(false);
    })();
  }, [selectedWarehouse?.value]);

  useEffect(() => {
    if (dataFetched) {
      let initialScrollPosition: any = getKey(
        listPaymentToSeller.scrollPosition
      );
      if (initialScrollPosition) {
        ElementAnimateUtil.scrollTo(parseInt(initialScrollPosition));
        setDataFetched(false);
        setKey(listPaymentToSeller.search, "", false);
      }
      removeKey(listSellerFinanceDetails.currentTab);
      removeKey(listSellerFinanceDetails.filter);
    }
  }, [dataFetched]);

  const fetchPayment = async (
    pageNo: number,
    limit: number,
    searchValue: string
  ) => {
    setFetchLoading(true)
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "_createdAt",
      sortOrder: -1,
      needCount: true,
      searchTerm: searchValue,
    };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      seller.payoutProfiles,
      sellerJSON.listPayoutProfiles(params),
      "",
      "",
      "",
      "",
      PaymentToSellers,
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response) {
      setTotalRecords(response.total);
      setPaymentData(response.records);
    }
    setFetchLoading(false)
  };
  const handleCurrentPage = async (val: number) => {
    setPage(val);
    setKey(listPaymentToSeller.page, val);
    setLoading(true);
    await fetchPayment(val, pageLimit, search);
    setLoading(false);
  };
  const handleNextPage = async (val: number) => {
    setPage(val + 1);
    setLoading(true);
    setKey(listPaymentToSeller.page, val + 1);
    await fetchPayment(val + 1, pageLimit, search);
    setLoading(false);
  };
  const handlePreviousPage = async (val: number) => {
    setPage(val - 1);
    setKey(listPaymentToSeller.page, val - 1);
    setLoading(true);
    await fetchPayment(val - 1, pageLimit, search);
    setLoading(false);
  };
  const handlePageLimit = async (event: any) => {
    setPage(1);
    await setPageLimit(parseInt(event.target.value));
    setKey(listPaymentToSeller.page, 1);
    setKey(listPaymentToSeller.limit, parseInt(event.target.value));
    setLoading(true);
    await fetchPayment(1, event.target.value, search);
    setLoading(false);
  };

  const debounce = useDebounce(fetchPayment, 500);

  const handleSearch = async (value: any) => {
    setSearch(value);
    setKey(listPaymentToSeller.page, 1);
    setKey(listPaymentToSeller.search, value, false);
    setPage(1);
    await debounce(1, pageLimit, value);
  };

  const goToVendorFinanceDetails = (paymentVal: any) => {
    let currentScroll = window.scrollY;
    setKey(listPaymentToSeller.scrollPosition, currentScroll);
    setKey(listPaymentToSeller.search, search, false);
    navigateTo(
      "/UProcure/vendors/payments-to-vendors/vendor-finance-details",
      {
        state: {
          id: paymentVal?._id,
          ownerName: paymentVal?.ownerName,
          name: paymentVal?.ownerName,
          image: paymentVal?.image,
          KYC: paymentVal?.KYC,
          ...paymentVal,
        },
      }
    );
  };

  const columns: ColumnDef<any>[] = [
    {
      header: "Supplier name",
      accessorKey: "ownerName",
      cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div className="symbol symbol-50px border me-3">
            <img
              width={50}
              height={50}
              src={row.original?.image}
              alt=""
              className="object-fit-contain"
            />
          </div>
          <span className="fs-15 fw-600">{row.original?.ownerName}</span>
        </div>
      ),
    },
    {
      header: "Total sales",
      accessorKey: "totalSales",
      cell: ({ row }) => (
        <span className="fs-15 fw-600">
          {"TSh " + Method.getGeneralizedAmount(row.original?.totalSales)}
        </span>
      ),
    },
    {
      header: "Commission",
      accessorKey: "totalSalesCommission",
      cell: ({ row }) => (
        <span className="fs-15 fw-600">
          {"TSh " +
            Method.getGeneralizedAmount(row.original?.totalSalesCommission)}
        </span>
      ),
    },
    {
      header: "Payments completed",
      accessorKey: "totalSalesPaid",
      cell: ({ row }) => (
        <span className="fs-15 fw-600">
          {"TSh " + Method.getGeneralizedAmount(row.original?.totalSalesPaid)}
        </span>
      ),
    },
    {
      header: "Payments due",
      accessorKey: "paymentsDue",
      cell: ({ row }) => (
        <span className="fs-15 fw-600 text-danger">
          {"TSh " +
            Method.getGeneralizedAmount(
              row.original?.totalSales -
              row.original?.totalSalesCommission -
              row.original?.totalSalesPaid
            )}
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="p-5">
        <Row className="align-items-center g-5">
          <Col xs>
            <h1 className="fs-22 fw-bolder mb-0">Payments to suppliers</h1>
          </Col>
          {showDownload ? (
            <>
              <SellerPayoutReport
                show={showDownload}
                onHide={() => {
                  setShowDownload(false);
                }}
                moduleName={PaymentToSellers}
              />
            </>
          ) : (
            <></>
          )}
          <Col xs="auto">
            <div className="d-flex align-items-center min-md-264px position-relative">
              <KTSVG
                path="/media/icons/duotune/general/gen021.svg"
                className="svg-icon-3 position-absolute ms-4"
              />
              <input
                type="text"
                id="kt_filter_search"
                className="form-control form-control-custom min-h-50px ps-12 text-dark"
                placeholder="Search by supplier name…"
                value={search}
                onChange={(event) => {
                  handleSearch(event.target.value.trimStart());
                }}
              />
            </div>
          </Col>
          {
            Method.hasPermission(PermissionModule.PaymentToVendors,Permissions.Download,currentUser)&&
          <Col xs="auto">
            <div className="d-flex align-items-center min-md-264px position-relative">
              {" "}
              <Button className="uw-btn uw-btn-primary uw-btn-lg"
                variant="primary"
                size="lg"
                onClick={() => {
                  setShowDownload(true);
                }}
              >
                Download report
              </Button>
            </div>
            <></>
          </Col>
          }
        </Row>
        <Card className="border border-r10px mt-7">
          <Card.Body className="p-0">
            <CustomTable
              data={paymentData}
              columns={columns}
              loading={fetchLoading || loading}
              onRowClick={canViewDetails ? goToVendorFinanceDetails : undefined}
            />
          </Card.Body>
        </Card>
        {!fetchLoading ? (
          <>
            {paymentData?.length ? (
              <Pagination
                totalRecords={totalRecords}
                currentPage={page}
                handleCurrentPage={(event: any) => {
                  handleCurrentPage(event);
                }}
                handleNextPage={(event: any) => {
                  handleNextPage(event);
                }}
                handlePreviousPage={(event: any) => {
                  handlePreviousPage(event);
                }}
                pageLimit={pageLimit}
                handlePageLimit={(event: any) => {
                  handlePageLimit(event);
                }}
              />
            ) : (
              <></>
            )}
          </>
        ) : (
          <></>
        )}
      </div>
    </>
  );
};
export default PaymentsToVendors;
